import React, { memo, useState, useRef, useEffect, useReducer, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    useTheme,
    createUseStyles,
    Modal,
    Text,
    Icon,
    Button,
    Menus,
    Tooltip,
} from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { deleteExtraFieldTab, deleteExtraField, updateExtraFieldsOrder } from 'lib/services/fields';
import {
    getFilterSchema,
    getColumns,
    getFilteredList,
    getTabsOptions,
    getDynamicFieldOptions,
    ENABLED_DUPLICATE_ENTITIES,
} from '../lib';
import { subscribe } from 'lib/EventBuser';
import { FIELDS_REFRESH } from 'lib/events';
import { showErrorToast } from 'lib/errors';
import { FIELD_TYPE_TAB, FIELD_TYPE_STANDARD, FIELD_TYPE_EXTRA } from 'lib/constants';
import { getParameter } from 'lib/services/parameters';

import SectionFilter from 'components/SectionFilter';
import SectionList from 'components/SectionList';
import SectionToolbar from 'components/SectionToolbar';
import SectionToolbarSearch from 'components/SectionToolbar/SectionToolbarSearch';
import FiltersButton, { getNumberFilters } from 'components/Filters/FiltersButton';
import { intercomHideForList } from 'utils/custom';
import { getFieldTypeIcon } from 'utils/icons';
import { successToast } from 'utils/toast';

import FieldsTabs from './FieldsTabs';
import FieldsIcon from '../icons/FieldsIcon';

import styles from './styles';

const useStyles = createUseStyles(styles, 'FieldsLists');

const initialState = {
    list: [],
    filteredList: [],
    isLoading: true,
    hasError: false,
    isFiltersOpen: false,
    removeModal: {
        isLoading: false,
        isOpen: false,
        row: null,
        tabWithFields: false,
    },
    filterSchema: [],
    numberFilters: 0,
    haveFilters: false,
    generalFilterValue: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...state,
                list: action.list,
                filteredList: action.filteredList,
                isLoading: false,
            };
        case 'setSchemaOptions':
            let newFilterSchema = JSON.parse(JSON.stringify(state.filterSchema));

            const optionsMap = {
                branches: action.branches,
                tabId: action.tabOptions,
                dynamicField: action.dynamicFieldOptions,
            };

            newFilterSchema.forEach((item) => {
                const id = item.id;
                if (optionsMap[id]) item.fields[0].inputProps.options = optionsMap[id];
            });

            return {
                ...state,
                filterSchema: newFilterSchema,
            };
        case 'setFilteredList':
            return {
                ...state,
                filteredList: action.filteredList,
                numberFilters: action.numberFilters,
                haveFilters: action.haveFilters,
            };
        case 'setGeneralFilterValue':
            return {
                ...state,
                filteredList: action.filteredList,
                generalFilterValue: action.generalFilterValue,
            };
        case 'setError':
            return { ...state, hasError: action.error, isLoading: false };
        case 'setLoading':
            return {
                ...state,
                isLoading: action.value,
                filteredList: action.resetList ? [] : state.filteredList,
            };
        case 'setIsFiltersOpen':
            return {
                ...state,
                isFiltersOpen: action.value,
            };
        case 'setRemoveModal':
            return {
                ...state,
                removeModal: action.value,
            };
        default:
            throw new Error('No action provided');
    }
}

const FieldsLists = ({
    entity,
    activeTab,
    activeShrinkedTab,
    branches,
    onChangeTab,
    onChangeShrinked,
    onEditStandardField,
    onCreateExtraField,
    onEditExtraField,
    onCreateFieldsTab,
    onEditFieldsTab,
    onReorderTabs,
    fieldsData,
    entityData,
    getEntityData,
    onReorderExtraFields,
    idSetupRole,
    activeRowId,
    setActiveRowId,
    entityLogName,
    entityLogSubmodule,
    overrides,
    onUpdate,
    onOpenUpgradeTabsInfo,
}) => {
    const theme = useTheme();
    const classes = useStyles();
    const isFirstTime = useRef(false);
    const currentEntity = useRef(entity);
    const columnIDsToHide = useMemo(() => {
        let columnIDsToHide = overrides?.columnIDsToHide;
        return columnIDsToHide && Array.isArray(columnIDsToHide) ? columnIDsToHide : null;
    }, [overrides]);
    const {
        t,
        language,
        permissions,
        logEvent,
        overridePermissions,
        isWeb,
        canCreateTabs,
        customGeneralTab,
        showCrud,
        tracking,
    } = useWeb5Context({
        overrides,
    });

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        filterSchema: getFilterSchema(t, columnIDsToHide),
    });
    const [intercomRef, setIntercomRef] = useState('show');
    const filterValues = useRef();
    const entities = useRef();
    const tabs = useRef();

    const setEntities = useCallback((value) => {
        entities.current = value;
    }, []);

    const callGetEntityData = useCallback(() => {
        dispatch({
            type: 'setLoading',
            value: true,
            resetList: entity !== currentEntity.current,
        });
        currentEntity.current = entity;

        Promise.all([getParameter('implementation', 'CheckDuplicateFields'), getEntityData(entity)])
            .then(([parameter, res]) => {
                // Checking duplicate param is disabled
                if (parameter?.data?.value === '0' || !parameter?.data?.value)
                    res.list.map((item) => (item.checkDuplicates = false));
                dispatch({
                    type: 'init',
                    list: res.list,
                    filteredList: filterValues.current
                        ? getFilteredList(res.list, filterValues.current)
                        : res.list,
                });
            })
            .catch((err) => {
                console.warn(err);
                dispatch({ type: 'setError', value: true });
            })
            .then(() => (isFirstTime.current = true))
            .then(() => dispatch({ type: 'setLoading', value: false }));
    }, [entity, getEntityData]);

    useEffect(() => callGetEntityData(), [callGetEntityData]);

    useEffect(() => {
        if (!branches || !entityData?.list || !entityData?.fields?.tab || !isFirstTime.current)
            return;
        isFirstTime.current = false;
        const tabOptions = getTabsOptions(entityData?.fields?.tab, t, customGeneralTab) || [];
        const dynamicFieldOptions = getDynamicFieldOptions(entityData?.list);
        dispatch({
            type: 'setSchemaOptions',
            branches,
            tabOptions,
            dynamicFieldOptions,
        });
    }, [t, language, state, entityData, branches, customGeneralTab]);

    useEffect(() => {
        return subscribe(FIELDS_REFRESH, () => callGetEntityData());
    }, [callGetEntityData]);

    // Create action
    const onClickCreateFieldsTab = useCallback(() => {
        onCreateFieldsTab(entities.current);
        setTimeout(() => {
            document.dispatchEvent(new Event('click'));
        });
    }, [onCreateFieldsTab]);

    const onClickCreateExtraField = useCallback(() => {
        onCreateExtraField(entities.current);
        setTimeout(() => {
            document.dispatchEvent(new Event('click'));
        });
        tracking?.create && tracking.create();
    }, [onCreateExtraField, tracking]);

    // Edit action
    const onEdit = useCallback(
        (row) => {
            switch (row.fieldType) {
                case FIELD_TYPE_TAB:
                    onEditFieldsTab(row, entities.current);
                    break;
                case FIELD_TYPE_STANDARD:
                    onEditStandardField(row, entities.current);
                    tracking?.update && tracking.update();
                    break;
                case FIELD_TYPE_EXTRA:
                    onEditExtraField(row, entities.current, tabs.current, entity);
                    tracking?.update && tracking.update();
                    break;

                default:
                    break;
            }
        },
        [entity, onEditExtraField, onEditFieldsTab, onEditStandardField, tracking],
    );

    const groupBy = (array, key) => {
        return array.reduce((result, obj) => {
            if (obj[key]) (result[obj[key]] = result[obj[key]] || []).push(obj.idDb);
            return result;
        }, {});
    };

    // Duplicate action
    const onDuplicate = useCallback(
        (row) => {
            logEvent &&
                logEvent({
                    event: entityLogName,
                    submodule: entityLogSubmodule,
                    functionality: 'duplicate',
                });

            onEditExtraField(row, entities.current, tabs.current, entity, true);

            onUpdate && onUpdate();
        },
        [entity, entityLogName, entityLogSubmodule, logEvent, onEditExtraField, onUpdate],
    );

    // Reorder tabs action
    const onReorder = useCallback(
        (row) => {
            onReorderTabs(entity, row);
        },
        [entity, onReorderTabs],
    );

    // Remove action
    const onCloseRemoveModal = useCallback(() => {
        setActiveRowId(null);
        dispatch({
            type: 'setRemoveModal',
            value: {
                isLoading: false,
                isOpen: false,
                row: state?.removeModal?.row,
                tabWithFields: state?.removeModal?.tabWithFields,
            },
        });
    }, [state, setActiveRowId]);

    const onRemove = useCallback(
        (row) => {
            setActiveRowId(row.id);
            let tabWithFields = false;
            const idGroups = groupBy(state?.list, 'tabId');
            if (row?.fieldType === FIELD_TYPE_TAB && idGroups[row.idDb]) tabWithFields = true;
            dispatch({
                type: 'setRemoveModal',
                value: { isLoading: false, isOpen: true, row, tabWithFields },
            });
            tracking?.delete && tracking.delete();
        },
        [state, setActiveRowId, tracking],
    );

    const onConfirmRemoveModal = useCallback(() => {
        dispatch({
            type: 'setRemoveModal',
            value: { isLoading: true, isOpen: true, row: state.removeModal.row },
        });

        if (state.removeModal.row.fieldType === FIELD_TYPE_TAB) {
            deleteExtraFieldTab(state.removeModal.row.idDb)
                .then(() => {
                    successToast({
                        t,
                        text: t('cfm_success_delete_tab'),
                    });
                    callGetEntityData();
                    onUpdate && onUpdate();
                })
                .catch((error) => showErrorToast(t, error))
                .then(() => onCloseRemoveModal());
        } else {
            logEvent({
                event: entityLogName,
                submodule: entityLogSubmodule,
                functionality: 'delete',
            });

            deleteExtraField(state.removeModal.row.idDb)
                .then(() => {
                    successToast({
                        t,
                        text: t('cfm_success_delete_field'),
                    });
                    callGetEntityData();
                    onUpdate && onUpdate();
                })
                .catch((error) => {
                    showErrorToast(t, error);
                })
                .then(() => {
                    onCloseRemoveModal();
                });
        }
    }, [
        logEvent,
        entityLogName,
        entityLogSubmodule,
        state.removeModal.row,
        t,
        callGetEntityData,
        onCloseRemoveModal,
        onUpdate,
    ]);

    const tableParams = useMemo(() => {
        return {
            onRowDrag: (params) => params.data.fieldType === FIELD_TYPE_EXTRA,
        };
    }, []);

    const getAvatarSvg = useCallback((data) => {
        return getFieldTypeIcon(data.cellType);
    }, []);

    // List configuration
    const columns = useMemo(() => {
        let columns = getColumns({
            t,
            params: tableParams,
            onEdit,
            getAvatarSvg,
            hasFilters: state.haveFilters,
            hasGeneralFilter: state.generalFilterValue,
            entity,
            permissions: permissions?.extraFields,
            overridePermissions,
            isEnabledDuplicateEntities: ENABLED_DUPLICATE_ENTITIES,
            columnIDsToHide,
        });

        return columns;
    }, [
        t,
        tableParams,
        onEdit,
        getAvatarSvg,
        state.haveFilters,
        state.generalFilterValue,
        entity,
        permissions?.extraFields,
        overridePermissions,
        columnIDsToHide,
    ]);

    const actions = useMemo(() => {
        return [
            (row) => {
                if (
                    row.fieldType === FIELD_TYPE_EXTRA &&
                    ((overridePermissions && row?.isEditable) ||
                        (permissions?.extraFields?.update &&
                            ((row?.isEditable && !permissions?.extraFields?.userUpdate) ||
                                permissions?.extraFields?.userUpdate)))
                )
                    return {
                        icon: (
                            <Icon
                                className="fields__edit-btn"
                                onClick={() => {
                                    setIntercomRef(false);
                                    onEdit(row, entities);
                                }}
                                name="edit"
                            />
                        ),
                        tooltip: { content: t('cfm_action_edit') },
                    };
                if (
                    row.fieldType === FIELD_TYPE_STANDARD &&
                    (overridePermissions || permissions?.extraFields?.updateStandard)
                )
                    return {
                        icon: (
                            <Icon
                                className="fields__edit-btn"
                                onClick={() => {
                                    setIntercomRef(false);
                                    onEdit(row, entities);
                                }}
                                name="edit"
                            />
                        ),
                        tooltip: { content: t('cfm_action_edit') },
                    };

                if (row.fieldType === FIELD_TYPE_TAB && row.idDb !== -1)
                    return {
                        icon: (
                            <Icon
                                className="fields__edit-btn"
                                onClick={() => {
                                    setIntercomRef(false);
                                    onEdit(row, entities);
                                }}
                                name="edit"
                            />
                        ),
                        tooltip: { content: t('cfm_action_edit') },
                    };
            },
            (row) => {
                if (row.fieldType === FIELD_TYPE_TAB && row.idDb !== -1)
                    return {
                        icon: (
                            <Icon
                                className="fields__reorder-btn"
                                onClick={() => onReorder(row)}
                                name="draggableSort"
                            />
                        ),
                        tooltip: { content: t('cfm_label_sort_tabs') },
                    };
            },
            (row) => {
                if (
                    row.fieldType === FIELD_TYPE_EXTRA &&
                    (overridePermissions || permissions?.extraFields?.create)
                )
                    return {
                        icon: (
                            <Icon
                                className="fields__duplicate-btn"
                                onClick={() => onDuplicate(row)}
                                name="duplicate"
                            />
                        ),
                        tooltip: { content: t('cfm_action_duplicate') },
                    };
            },
            (row) => {
                if (row.fieldType === FIELD_TYPE_TAB && row.idDb !== -1)
                    return {
                        icon: (
                            <Icon
                                className="fields__delete-btn"
                                onClick={() => onRemove(row)}
                                name="trash"
                            />
                        ),
                        tooltip: { content: t('cfm_action_delete') },
                    };
                if (
                    row.fieldType === FIELD_TYPE_EXTRA &&
                    ((overridePermissions && row?.isEditable) ||
                        (permissions?.extraFields?.delete &&
                            ((row?.isEditable && !permissions?.extraFields?.userUpdate) ||
                                permissions?.extraFields?.userUpdate)))
                )
                    return {
                        icon: (
                            <Icon
                                className="fields__delete-btn"
                                onClick={() => onRemove(row)}
                                name="trash"
                            />
                        ),
                        tooltip: { content: t('cfm_action_delete') },
                    };
            },
        ];
    }, [permissions, overridePermissions, t, onEdit, onReorder, onDuplicate, onRemove]);

    const openFilters = useCallback(() => {
        dispatch({ type: 'setIsFiltersOpen', value: !state.isFiltersOpen });
    }, [state.isFiltersOpen]);

    const onChangeFilters = useCallback(
        (values) => {
            filterValues.current = { ...filterValues.current, ...values };
            const numberFilters = getNumberFilters(values);

            if (numberFilters > 0) {
                logEvent({
                    event: entityLogName,
                    submodule: entityLogSubmodule,
                    functionality: 'filter',
                });
            } else {
                filterValues.current = {};
            }

            dispatch({
                type: 'setFilteredList',
                filteredList: getFilteredList(fieldsData[entity]?.list, {
                    ...values,
                    name: state.generalFilterValue?.value,
                }),
                numberFilters,
                haveFilters: numberFilters > 0,
            });
        },
        [
            entity,
            entityLogName,
            entityLogSubmodule,
            fieldsData,
            logEvent,
            state.generalFilterValue?.value,
        ],
    );

    const generalSearchOptions = useMemo(
        () => state.list.map((el) => ({ ...el, label: el['name'], value: el.id })),
        [state.list],
    );

    const onChangeGeneralSearch = useCallback(
        (value, info) => {
            filterValues.current = { ...filterValues.current, name: null };
            if (value && info && info.action === 'select-option') {
                onEdit(value);
            }
            if (!value && info && info.action === 'clear') {
                dispatch({
                    type: 'setGeneralFilterValue',
                    filteredList: getFilteredList(fieldsData[entity]?.list, {
                        ...filterValues.current,
                        name: null,
                    }),
                    generalFilterValue: null,
                });
            }
        },
        [entity, fieldsData, onEdit],
    );

    const onBlurGeneralSearch = useCallback(
        (value) => {
            logEvent({
                event: entityLogName,
                submodule: entityLogSubmodule,
                functionality: 'search',
            });

            filterValues.current = { ...filterValues.current, name: value };
            dispatch({
                type: 'setGeneralFilterValue',
                filteredList: getFilteredList(fieldsData[entity]?.list, {
                    ...filterValues.current,
                    name: value,
                }),
                generalFilterValue: value ? { label: value, value } : null,
            });
        },
        [entity, entityLogName, entityLogSubmodule, fieldsData, logEvent],
    );

    const loadGeneralSearchOptions = useCallback(
        (value) =>
            Promise.resolve(
                generalSearchOptions?.filter((current) =>
                    current.label?.toLowerCase().includes(value?.toLowerCase()),
                ),
            ),
        [generalSearchOptions],
    );

    const onChangeCreate = useCallback(
        ({ value }) => {
            if (value === 'create_extra_field') onClickCreateExtraField();
            if (value === 'create_tab') {
                if (isWeb && !canCreateTabs) onOpenUpgradeTabsInfo();
                else onClickCreateFieldsTab();
            }
        },
        [
            onClickCreateExtraField,
            onClickCreateFieldsTab,
            canCreateTabs,
            onOpenUpgradeTabsInfo,
            isWeb,
        ],
    );

    const createOptions = useMemo(
        () => [
            {
                title: t('cfm_title_create_extra_field'),
                onClick: () => onChangeCreate({ value: 'create_extra_field' }),
                className: 'fields__create-extra-field-btn',
            },
            {
                title: t('cfm_action_create_tab'),
                onClick: () => onChangeCreate({ value: 'create_tab' }),
                className: 'fields__create-tab-btn',
            },
        ],
        [t, onChangeCreate],
    );

    const searchPlaceholder = useMemo(
        () => `${t('cfm_placeholder_search')} ${t('cfm_label_field_name').toLowerCase()}`,
        [t],
    );

    const toolbarItems = useMemo(() => {
        const items = [];
        const supportLink = isWeb
            ? 'link_custom_fields_help_center_web'
            : 'link_custom_fields_help_center';
        items.push({
            component: (
                <SectionToolbarSearch
                    className="fields__toolbar__search"
                    value={state.generalFilterValue}
                    placeholder={searchPlaceholder}
                    loadOptions={loadGeneralSearchOptions}
                    onChange={onChangeGeneralSearch}
                    onBlurSearch={onBlurGeneralSearch}
                />
            ),
            position: 'left',
        });

        items.push({
            component: (
                <Tooltip placement="top" content={<span>{t('label_help_center')}</span>}>
                    <Button
                        type="terciary"
                        icon="helpOutline"
                        onClick={() => window.open(t(supportLink), '_blank')}
                    />
                </Tooltip>
            ),
            position: 'right',
        });

        items.push({
            component: (
                <FiltersButton
                    haveFilters={state.haveFilters}
                    numberFilters={state.numberFilters}
                    openFilters={openFilters}
                />
            ),
            position: 'right',
        });

        if (overridePermissions || permissions?.extraFields?.create) {
            items.push({
                component: (
                    <Menus
                        items={createOptions}
                        icon={
                            <Button
                                icon="arrowDropDown"
                                iconPosition="right"
                                className="fields__toolbar__create-btn"
                            >
                                {t('cfm_action_create')}
                            </Button>
                        }
                    />
                ),
                position: 'right',
            });
        }

        return items;
    }, [
        isWeb,
        state.generalFilterValue,
        state.haveFilters,
        state.numberFilters,
        searchPlaceholder,
        loadGeneralSearchOptions,
        onChangeGeneralSearch,
        onBlurGeneralSearch,
        t,
        openFilters,
        overridePermissions,
        permissions?.extraFields?.create,
        createOptions,
    ]);

    const rowClassRules = useMemo(
        () => ({
            'row-tab': function (params) {
                return params?.data?.fieldType === FIELD_TYPE_TAB;
            },
        }),
        [],
    );

    const onCloseFilters = useCallback(
        () => dispatch({ type: 'setIsFiltersOpen', value: false }),
        [],
    );

    const ReadOnlyHeader = useCallback(
        () => <Icon className="fields__readonly-icon" name="lock" color={theme.colors.grey[700]} />,
        [theme.colors.grey],
    );

    const VisibleHeader = useCallback(
        () => (
            <Icon
                className="fields__visible-icon"
                name="visibilityOn"
                color={theme.colors.grey[700]}
            />
        ),
        [theme.colors.grey],
    );

    const MandatoryHeader = useCallback(
        () => (
            <Icon
                className="fields__asterisk-icon"
                name="asterisk"
                color={theme.colors.grey[700]}
            />
        ),
        [theme.colors.grey],
    );

    const DuplicatesHeader = useCallback(
        () => (
            <Icon
                className="fields__duplicates-icon"
                name="duplicates"
                color={theme.colors.grey[700]}
            />
        ),
        [theme.colors.grey],
    );

    const emptyViewProps = useMemo(
        () => ({
            icon: <FieldsIcon color={theme.colors.grey[100]} />,
        }),
        [theme],
    );

    const headerComps = useMemo(
        () => ({
            ReadOnlyHeader,
            VisibleHeader,
            MandatoryHeader,
            DuplicatesHeader,
        }),
        [DuplicatesHeader, MandatoryHeader, ReadOnlyHeader, VisibleHeader],
    );

    const modalOverrides = {
        confirmButton: {
            type: 'primary-error',
            isLoading: state.removeModal.isLoading,
        },
    };

    const getDeleteBody = useMemo(() => {
        if (
            state?.removeModal?.row?.fieldType === FIELD_TYPE_TAB &&
            state?.removeModal?.tabWithFields
        ) {
            return t('cfm_label_can_not_delete_tab', [state?.removeModal?.row?.description]);
        } else if (
            state?.removeModal?.row?.fieldType === FIELD_TYPE_TAB &&
            !state?.removeModal?.tabWithFields
        ) {
            return t('cfm_label_confirm_delete_tab', [state?.removeModal?.row?.description]);
        } else {
            return t('cfm_label_confirm_delete_field');
        }
    }, [state, t]);

    const dragGroups = useMemo(() => {
        const groups = { ...entityData?.extraOrder };
        Object.keys(groups).forEach((key) => {
            groups[key] = groups[key].map((el) => `e${el}`);
        });
        return { groups, groupsKey: 'tabId' };
    }, [entityData]);

    const onDragEnd = useCallback(
        (event, data) => {
            logEvent({
                event: entityLogName,
                submodule: entityLogSubmodule,
                functionality: 'sortField',
            });

            const group = [...entityData.extraOrder[event.node.data.tabId]];
            const from = group.indexOf(event.node.data.idDb);
            const to = group.indexOf(data.rowDataBefore[event.overIndex].idDb);
            const element = group.splice(from, 1)[0];

            group.splice(to, 0, element);

            const groupOrder = group.map((el, i) => ({ id: el, order: i }));

            return updateExtraFieldsOrder(groupOrder).then(() => {
                onReorderExtraFields(event.node.data.tabId, group);
            });
        },
        [entityData?.extraOrder, entityLogName, entityLogSubmodule, logEvent, onReorderExtraFields],
    );

    const getRowNodeId = useCallback((data) => {
        return data.id;
    }, []);

    const getActiveRowId = useCallback(() => {
        return activeRowId;
    }, [activeRowId]);

    const placeholderOptions = useMemo(() => ({ avatars: false }), []);

    const onCellMouseOver = useCallback(
        (data) => {
            const hideShow = intercomHideForList(data, state.filteredList, 4);
            setIntercomRef(hideShow);
            window.userflow?.setResourceCenterLauncherHidden(hideShow === 'hide');
        },
        [state.filteredList],
    );

    const listProps = useMemo(
        () => ({
            id: `fields-list-${state.data?.entity?.value}`,
            onCellMouseOver: onCellMouseOver,
            className: classes.list,
            columns: columns,
            rows: state.filteredList,
            actions: actions,
            isLoading: state.isLoading,
            hasError: state.hasError,
            headerHeight: 40,
            rowHeight: 40,
            rowClassRules: rowClassRules,
            emptyViewProps: emptyViewProps,
            components: headerComps,
            dragGroups: dragGroups,
            onDragEnd: onDragEnd,
            useDragRows: !state.haveFilters && !state.generalFilterValue,
            getRowNodeId: getRowNodeId,
            activeRowId: getActiveRowId(),
            useLoaderPlaceholder: true,
            placeholderOptions: placeholderOptions,
            intercomRef: intercomRef,
            withoutOffset: true,
        }),
        [
            actions,
            classes.list,
            columns,
            dragGroups,
            emptyViewProps,
            getActiveRowId,
            getRowNodeId,
            headerComps,
            intercomRef,
            onCellMouseOver,
            onDragEnd,
            placeholderOptions,
            rowClassRules,
            state.data?.entity?.value,
            state.filteredList,
            state.generalFilterValue,
            state.hasError,
            state.haveFilters,
            state.isLoading,
        ],
    );

    useEffect(() => {
        setTimeout(() => {
            showCrud && fieldsData?.[entity] && onClickCreateExtraField();
        });
    }, [showCrud, onClickCreateExtraField, fieldsData, entity]);

    return (
        <div className={classes.root}>
            <SectionFilter
                schema={state.filterSchema}
                isOpen={state.isFiltersOpen}
                onChange={onChangeFilters}
                onClose={onCloseFilters}
                id="fields__section-filter__section"
            />
            <FieldsTabs
                onChange={onChangeTab}
                onChangeShrinked={onChangeShrinked}
                activeShrinkedTab={activeShrinkedTab}
                activeTab={activeTab}
                setEntities={setEntities}
                idSetupRole={idSetupRole}
                overrides={overrides}
            />
            <SectionToolbar items={toolbarItems} />
            {overrides && overrides.tablePreComponent}
            <div className={classes.listContainer}>
                <SectionList {...listProps} />
            </div>
            <Modal
                title={
                    state.removeModal.tabWithFields
                        ? t('cfm_action_delete_tab')
                        : t('action_delete')
                }
                size="small"
                isOpen={state.removeModal.isOpen}
                onRequestClose={onCloseRemoveModal}
                onCancel={!state.removeModal.tabWithFields ? onCloseRemoveModal : undefined}
                onConfirm={
                    state.removeModal.tabWithFields ? onCloseRemoveModal : onConfirmRemoveModal
                }
                cancelText={t('cfm_action_cancel')}
                confirmText={
                    state.removeModal.tabWithFields ? t('action_got_it') : t('cfm_action_accept')
                }
                overrides={modalOverrides}
            >
                <Text>{getDeleteBody}</Text>
            </Modal>
        </div>
    );
};

FieldsLists.propTypes = {
    entity: PropTypes.string.isRequired,
    activeTab: PropTypes.string,
    activeShrinkedTab: PropTypes.object,
    onEditStandardField: PropTypes.func,
    onCreateExtraField: PropTypes.func,
    onEditExtraField: PropTypes.func,
    onCreateFieldsTab: PropTypes.func,
    onEditFieldsTab: PropTypes.func,
    onFilter: PropTypes.func,
};

export default memo(FieldsLists);
